import { useState, useEffect, useRef } from "react";
import { DomEvent } from "leaflet";
import axios from "axios";
import classes from "./NodeBox.module.scss";
import ArmyForm from "../forms/ArmyForm";
import CloseButton from "../buttons/CloseButton";
import { nodes } from "../../data/nodes";


export const NodeBox = ({ node, armies, onClose, disabled, playersOverride, yesterdayPlayersMap }) => {
  // Collapsible state for Players section
  const [playersExpanded, setPlayersExpanded] = useState(true);
  const [players, setPlayers] = useState([]);
  const [playersLastUpdated, setPlayersLastUpdated] = useState(null);
  // Collapsible state for New arrivals section
  const [arrivalsExpanded, setArrivalsExpanded] = useState(true);
  // Get yesterday's players for this node from the map
  const yesterdayPlayers = yesterdayPlayersMap && yesterdayPlayersMap[node.id] ? yesterdayPlayersMap[node.id] : [];
  const arrivalsLoading = false;

  // Compute new arrivals: players here today but not yesterday (case-insensitive, robust)
  const yesterdaySet = new Set((yesterdayPlayers || []).map(p => String(p).toLowerCase()));
  const newArrivals = (players || []).filter(p => !yesterdaySet.has(String(p).toLowerCase()));
  const [activeArmy, setActiveArmy] = useState();
  const [creatingArmy, setCreatingArmy] = useState(false);
  const [cityData, setCityData] = useState(null);
  const [loading, setLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || '/api';
  const token = window.localStorage.getItem("loggedBLUser");

  const formatEuropeanDateTime = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const containerRef = useRef(null);
  useEffect(() => {
    if (containerRef.current) {
      DomEvent.disableScrollPropagation(containerRef.current);
    }
  }, [containerRef]);

  useEffect(() => {
    if (node.type !== "city") {
      setCityData(null);
      setPlayers([]);
      setPlayersLastUpdated(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Fetch city-data.json for city/county/kingdom info
    fetch("/city-data.json")
      .then((response) => {
        if (!response.ok) throw new Error("Failed to fetch city-data.json");
        return response.json();
      })
      .then((data) => {
        const result = findCityAndCountryInfo(data, node.name);
        setCityData(result);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching city-data.json:", error);
        setLoading(false);
      });

    // If playersOverride exists (history mode), use it
    if (Array.isArray(playersOverride)) {
      setPlayers(playersOverride);
      setPlayersLastUpdated(null);
      return;
    }

    // Otherwise fetch live players
    let cancelled = false;

    axios
      .get(
        `${apiUrl}/nodes/${node.id}`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      )
      .then((res) => {
        if (cancelled) return;
        setPlayers(Array.isArray(res.data.players) ? res.data.players : []);
        setPlayersLastUpdated(res.data.updated_date || null);
      })
      .catch(() => {
        if (cancelled) return;
        setPlayers([]);
        setPlayersLastUpdated(null);
      });

    return () => {
      cancelled = true;
    };
  }, [node, playersOverride]);

  const formattedPlayersLastUpdated = formatEuropeanDateTime(playersLastUpdated);

  useEffect(() => {
    console.log(yesterdayPlayers);
  }, [yesterdayPlayers]);

  const findCityAndCountryInfo = (data, cityName) => {
    // Iterate through countries
    for (const countryKey in data) {
      const country = data[countryKey];

      // Iterate through counties (comtes)
      const comtes = country.Comtes;
      if (comtes) {
        for (const comteKey in comtes) {
          const comte = comtes[comteKey];

          // Iterate through villages
          const villages = comte.Villages;
          if (villages) {
            for (const villageKey in villages) {
              const village = villages[villageKey];

              // Check if the village name matches
              if (village.Nom === cityName) {
                // Return all relevant data
                return {
                  cityInfo: {
                    name: village.Nom,
                    population: village.Population,
                    mayor: village.Maire,
                    region: village.Region,
                  },
                  countyInfo: {
                    name: comte.Nom,
                    population: comte.Population,
                    prestige: comte.Prestige,
                  },
                  countryInfo: {
                    name: country.Nom,
                    population: country.Population,
                  },
                };
              }
            }
          }
        }
      }
    }
    return null;
  };

  // Extract armies on this node (excluding scouts)
  const armiesHere = armies
    ? armies.filter((army) => army.node === node.id && army.status !== "scout")
    : [];

  // Build a map of player -> status (friend > enemy > neutral) for correct coloring
  // Build a map of player -> status (friend > enemy > neutral) for correct coloring
  // Use a Map for playerStatusMap to ensure all players are checked and colored
  const playerStatusMap = new Map();
  const statusPriority = { friend: 3, enemy: 2, neutral: 1 };
  armiesHere.forEach((army) => {
    const status = army.status;
    const setStatus = (player) => {
      if (!player) return;
      const key = String(player).toLowerCase();
      const current = playerStatusMap.get(key);
      if (!current || statusPriority[status] > statusPriority[current]) {
        playerStatusMap.set(key, status);
      }
    };
    setStatus(army.general);
    if (Array.isArray(army.soldiers)) {
      army.soldiers.forEach(setStatus);
    }
  });

  if (creatingArmy) {
    return (
      <ArmyForm
        nodes={nodes}
        nodeNumber={node.id}
        onClose={() => {
          setCreatingArmy(false);
          onClose();
        }}
        disabled={disabled}
      />
    );
  }

  if (activeArmy) {
    return (
      <ArmyForm
        key={`army-form-${activeArmy.id}`}
        nodes={nodes}
        onClose={() => {
          setActiveArmy(null);
          onClose();
        }}
        army={activeArmy}
        disabled={disabled || activeArmy.permission !== "edit"}
      />
    );
  }

  return (
      <div className={classes.nodeBox} ref={containerRef}>
        <CloseButton onClose={onClose} />
        <ul>
          <li>
            <span>
              <b>Node:</b> <span className={classes.prop}>{node.id}</span>
            </span>
            <span className={classes.prop}>
              {node.name} {node.walls && `(${node.walls})`}
            </span>
            {node.type === "city" && !loading && cityData && (
              <>
                <span>Population: {cityData.cityInfo.population}</span>
                <span>
                  Mayor:{" "}
                  <a
                    href={`https://www.renaissancekingdoms.com/FichePersonnage.php?login=${cityData.cityInfo.mayor}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {cityData.cityInfo.mayor}
                  </a>
                </span>
                <br />
                <span><b>County:</b></span>
                <span>{cityData.countyInfo.name}</span>
                <span>Prestige: {cityData.countyInfo.prestige.replace("&frac12", "½")}</span>
                <span>Population: {cityData.countyInfo.population}</span>
                <br />
                <span><b>Kingdom:</b></span>
                <span>{cityData.countryInfo.name}</span>
                <span>Population: {cityData.countryInfo.population}</span>
                <br />
              </>
            )}
          </li>
          <li>
            {!disabled && (
              <li>
                <div onClick={() => setCreatingArmy(true)} className={classes.btn}>
                  Add new army here
                </div>
              </li>
            )}
          </li>
          <li>
            <b>Armies here:</b>
            <ul className={classes.armies}>
              {armiesHere
                .sort((a, b) => {
                  const statusOrder = { friend: 1, enemy: 2, neutral: 3 };
                  const statusComparison = (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4);
                  if (statusComparison !== 0) return statusComparison;
                  return a.name.localeCompare(b.name);
                })
                .map((army) => (
                  <li
                    key={`army-${army.id}`}
                    className={classes[army.status] || ""}
                    onClick={() => setActiveArmy(army)}
                  >
                    {army.name}
                  </li>
                ))}
            </ul>
          </li>
          {armies.some((army) => army.node === node.id && army.status === "scout") && (
            <li>
              <br />
              <b>Scouts here:</b>
              <ul className={classes.armies}>
                {armies
                  .filter((army) => army.node === node.id && army.status === "scout") // Only scouts
                  .sort((a, b) => a.name.localeCompare(b.name)) // Sort scouts alphabetically
                  .map((army) => (
                    <li
                      key={`army-${army.id}`}
                      className={classes.scout || ""}
                      onClick={() => setActiveArmy(army)}
                    >
                      {army.name}
                    </li>
                  ))}
              </ul>
            </li>
          )}
          {players.length > 0 && (
            <>
              <br />
              <div style={{ display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }} onClick={() => setPlayersExpanded((v) => !v)}>
                <span style={{ marginRight: 6, fontSize: 14, display: "inline-flex", alignItems: "center" }}>
                  {playersExpanded ? (
                    <span style={{ display: "inline-block", transform: "rotate(90deg)", transition: "transform 0.2s" }}>&#9654;</span>
                  ) : (
                    <span style={{ display: "inline-block", transform: "rotate(0deg)", transition: "transform 0.2s" }}>&#9654;</span>
                  )}
                </span>
                <b>
                  Players here:
                  {formattedPlayersLastUpdated && ` (Updated: ${formattedPlayersLastUpdated})`}
                </b>
              </div>
              {playersExpanded && (
                <ul>
                  {players.map((player) => {
                    let playerClass = classes.playerLink;
                    const key = String(player).toLowerCase();
                    const status = playerStatusMap.get(key);
                    if (status && classes[status]) {
                      playerClass += " " + classes[status]; // friend/enemy/neutral
                    }
                    return (
                      <li key={player} style={{ all: "unset", display: "list-item" }}>
                        <a
                          href={`https://www.renaissancekingdoms.com/FichePersonnage.php?login=${player}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={playerClass}
                        >
                          {player}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
              {/* New arrivals section */}
              {!Array.isArray(playersOverride) && (
                <>
                  <div style={{ display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none", marginTop: 8 }} onClick={() => setArrivalsExpanded((v) => !v)}>
                    <span style={{ marginRight: 6, fontSize: 14, display: "inline-flex", alignItems: "center" }}>
                      {arrivalsExpanded ? (
                        <span style={{ display: "inline-block", transform: "rotate(90deg)", transition: "transform 0.2s" }}>&#9654;</span>
                      ) : (
                        <span style={{ display: "inline-block", transform: "rotate(0deg)", transition: "transform 0.2s" }}>&#9654;</span>
                      )}
                    </span>
                    <b>New arrivals:</b>
                    {arrivalsLoading && <span style={{ marginLeft: 8, fontSize: 12 }}>(loading...)</span>}
                  </div>
                  {arrivalsExpanded && (
                    <ul>
                      {arrivalsLoading ? (
                        <li style={{ fontStyle: "italic", color: "#888" }}>Loading...</li>
                      ) : newArrivals.length === 0 ? (
                        <li style={{ fontStyle: "italic", color: "#888" }}>No new arrivals</li>
                      ) : (
                        newArrivals.map((player) => {
                          let playerClass = classes.playerLink;
                          const key = String(player).toLowerCase();
                          const status = playerStatusMap.get(key);
                          if (status && classes[status]) {
                            playerClass += " " + classes[status];
                          }
                          return (
                            <li key={player} style={{ all: "unset", display: "list-item" }}>
                              <a
                                href={`https://www.renaissancekingdoms.com/FichePersonnage.php?login=${player}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={playerClass}
                              >
                                {player}
                              </a>
                            </li>
                          );
                        })
                      )}
                    </ul>
                  )}
                </>
              )}
            </>
          )}
        </ul>
      </div>
  );
};

export default NodeBox;
